import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import HomeCTA from "../HomeCTA/HomeCTA";
import "./ProjectDetail.css";

// Main Project Images
import solarInstallImg from "../../assets/PowerGridProjects/solar_install.webp";
import windEnergyImg from "../../assets/PowerGridProjects/wind_energy.webp";
import energyStorageImg from "../../assets/PowerGridProjects/energy_storage.webp";
import hydroPowerImg from "../../assets/PowerGridProjects/hydro_power.webp";

// Process Marquee Images
import process1 from "../../assets/PowerGridProjects/process_1.webp";
import process2 from "../../assets/PowerGridProjects/process_2.webp";
import process3 from "../../assets/PowerGridProjects/process_3.webp";
import process4 from "../../assets/PowerGridProjects/process_4.webp";

const allProjects = {
  "solar-installation": {
    slug: "solar-installation",
    title: "Solar Installation",
    subtitle: "Installed a rooftop solar system for a residential home.",
    mainImage: solarInstallImg,
    client: "Canal Villere",
    type: "Residential",
    completed: "January 30, 2026",
    budget: "$30,000",
    location: "Russell Street Quincy, MA",
    goals:
      "Our project goals are centered around delivering a high-performance, future-ready energy solution that not only meets current power demands but also ensures long-term sustainability and efficiency. We focus on reducing electricity costs, improving energy reliability, and minimizing environmental impact through the use of advanced renewable technologies. By combining smart system design, quality components, and expert execution, this project aims to create a seamless and dependable energy experience for users.",
    overview:
      "This project represents a major step toward sustainable living by integrating advanced renewable energy solutions into a modern residential community. The objective was to reduce dependency on traditional electricity sources, lower long-term energy costs, and create a reliable power system capable of supporting daily needs without interruption.",
    challenge:
      "Before the implementation of this project, the site faced several critical energy challenges, including high electricity costs, frequent power interruptions, and heavy reliance on traditional grid systems. These issues not only affected daily operations but also increased long-term expenses and reduced overall efficiency, making it essential to find a more reliable and sustainable solution.",
    solution:
      "To address these challenges, we designed and implemented a customized renewable energy solution tailored to the specific needs of the project. By integrating high-efficiency solar panels, advanced inverter systems, and reliable battery storage, we created a smart and sustainable energy system that ensures consistent performance, reduces operational costs, and enhances overall energy efficiency.",
  },
  "wind-energy": {
    slug: "wind-energy",
    title: "Wind Energy",
    subtitle: "Set up small wind turbines to power places off the grid.",
    mainImage: windEnergyImg,
    client: "Joshua Fowler",
    type: "Hydropower",
    completed: "February 15, 2026",
    budget: "$18,000",
    location: "Hill Valley Station, OR",
    goals:
      "Our objective was to harness micro-wind kinetic energy to establish an autonomous, clean electricity generation network for remote sites that operates reliably even in fluctuating weather conditions.",
    overview:
      "This wind energy installation features state-of-the-art low-wind turbine technology, ensuring continuous yield and lowering grid dependence for off-grid operations.",
    challenge:
      "The terrain required resilient engineering to withstand high wind gusts while maintaining consistent energy production without heavy grid transmission infrastructure.",
    solution:
      "We engineered an optimized turbine layout with smart pitch control and integrated storage, delivering stable voltage and lowering generation costs by 65%.",
  },
  "energy-storage": {
    slug: "energy-storage",
    title: "Energy Storage",
    subtitle: "Installed advanced battery storage for a facility.",
    mainImage: energyStorageImg,
    client: "Lucy Ryan",
    type: "Residential",
    completed: "March 10, 2026",
    budget: "$20,000",
    location: "Cascade Green Park, WA",
    goals:
      "To provide high-density battery storage capable of peak shaving, load balancing, and emergency backup power during outage periods.",
    overview:
      "Integrating high-efficiency lithium battery energy storage systems (BESS) designed for seamless dispatch and real-time power monitoring.",
    challenge:
      "Managing peak-load thermal surges and ensuring multi-cycle longevity with rapid response switching under 10 milliseconds.",
    solution:
      "Installed a smart modular storage rack with active liquid cooling and intelligent AI battery management software.",
  },
  "micro-hydropower": {
    slug: "micro-hydropower",
    title: "Micro Hydropower",
    subtitle: "Installed a rooftop solar system for a residential home.",
    mainImage: hydroPowerImg,
    client: "Lewis Parker",
    type: "Residential",
    completed: "March 25, 2026",
    budget: "$26,000",
    location: "Clearwater River Valley, ID",
    goals:
      "To construct an eco-friendly run-of-river hydro generation unit that preserves local water ecosystems while yielding reliable power.",
    overview:
      "Micro hydropower harnesses running water streams to deliver 24/7 baseload power with minimal physical and environmental footprint.",
    challenge:
      "Handling seasonal water velocity variations and ensuring fish-friendly, sediment-resistant intake structures.",
    solution:
      "Implemented a custom cross-flow turbine system with automated flow regulation and fish bypass channels.",
  },
};

const marqueeImagesList = [process1, process2, process3, process4];

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Scroll to top when slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const currentProject =
    (slug && allProjects[slug]) ||
    allProjects[slug?.replace("-kxcal", "")] ||
    allProjects["solar-installation"];

  // Related projects list (exclude current)
  const relatedProjects = Object.values(allProjects).filter(
    (p) => p.slug !== currentProject.slug
  ).slice(0, 2);

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.65, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  return (
    <div className="project-detail-page">
      {/* ====================================================================
          1. HEADER TITLE SECTION
          ==================================================================== */}
      <section className="detail-header-section">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            className="detail-header-wrap"
          >
            <h1 className="detail-main-title">{currentProject.title}</h1>
            <p className="detail-subtitle">{currentProject.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* ====================================================================
          2. MAIN BANNER THUMBNAIL
          ==================================================================== */}
      <section className="detail-hero-banner-section">
        <div className="container">
          <motion.div
            className="detail-banner-wrap"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <img
              src={currentProject.mainImage}
              alt={currentProject.title}
              className="detail-banner-img"
            />
          </motion.div>
        </div>
      </section>

      {/* ====================================================================
          3. GOALS, OVERVIEW & CLIENT METADATA
          ==================================================================== */}
      <section className="detail-content-section">
        <div className="container">
          <div className="detail-grid-layout">
            {/* Left: Text Sections */}
            <motion.div
              className="detail-left-text-block"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              variants={fadeUp}
            >
              <div className="detail-text-group">
                <h3 className="detail-block-heading">Project Goals</h3>
                <p className="detail-block-paragraph">{currentProject.goals}</p>
              </div>

              <div className="detail-text-group">
                <h3 className="detail-block-heading">Project Overview</h3>
                <p className="detail-block-paragraph">{currentProject.overview}</p>
              </div>
            </motion.div>

            {/* Right: Client Details Box */}
            <motion.div
              className="detail-right-meta-block"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              variants={fadeUp}
            >
              <div className="meta-row">
                <span className="meta-key">Client:</span>
                <span className="meta-val">{currentProject.client}</span>
              </div>
              <div className="meta-divider"></div>

              <div className="meta-row">
                <span className="meta-key">Type:</span>
                <span className="meta-val">{currentProject.type}</span>
              </div>
              <div className="meta-divider"></div>

              <div className="meta-row">
                <span className="meta-key">Completed:</span>
                <span className="meta-val">{currentProject.completed}</span>
              </div>
              <div className="meta-divider"></div>

              <div className="meta-row">
                <span className="meta-key">Budget:</span>
                <span className="meta-val">{currentProject.budget}</span>
              </div>
              <div className="meta-divider"></div>

              <div className="meta-row">
                <span className="meta-key">Location:</span>
                <span className="meta-val">{currentProject.location}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====================================================================
          4. AUTOMATIC SMOOTH HORIZONTAL MARQUEE
          ==================================================================== */}
      <section className="detail-marquee-section">
        <div className="marquee-outer-container">
          <div className="marquee-infinite-track">
            {/* Set 1 */}
            {marqueeImagesList.map((imgSrc, i) => (
              <div className="marquee-card-item" key={`m1-${i}`}>
                <img
                  src={imgSrc}
                  alt={`Process step ${i + 1}`}
                  className="marquee-img"
                />
              </div>
            ))}
            {/* Set 2 (for seamless loop) */}
            {marqueeImagesList.map((imgSrc, i) => (
              <div className="marquee-card-item" key={`m2-${i}`}>
                <img
                  src={imgSrc}
                  alt={`Process step ${i + 1}`}
                  className="marquee-img"
                />
              </div>
            ))}
            {/* Set 3 */}
            {marqueeImagesList.map((imgSrc, i) => (
              <div className="marquee-card-item" key={`m3-${i}`}>
                <img
                  src={imgSrc}
                  alt={`Process step ${i + 1}`}
                  className="marquee-img"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================================================================
          5. THE CHALLENGE & OUR SOLUTION
          ==================================================================== */}
      <section className="detail-challenge-section">
        <div className="container">
          <motion.div
            className="detail-challenge-block"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            variants={fadeUp}
          >
            <div className="detail-text-group">
              <h3 className="detail-block-heading">The Challenge</h3>
              <p className="detail-block-paragraph">{currentProject.challenge}</p>
            </div>

            <div className="detail-text-group">
              <h3 className="detail-block-heading">Our Solution</h3>
              <p className="detail-block-paragraph">{currentProject.solution}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ====================================================================
          6. READ MORE PROJECTS
          ==================================================================== */}
      <section className="detail-related-section">
        <div className="container">
          <motion.div
            className="related-heading-wrap"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-30px" }}
            variants={fadeUp}
          >
            <h2 className="related-title">Read More Projects</h2>
          </motion.div>

          <div className="related-grid-layout">
            {relatedProjects.map((relProj) => (
              <motion.div
                key={relProj.slug}
                className="related-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-30px" }}
                variants={fadeUp}
              >
                <div className="related-img-wrap">
                  <img
                    src={relProj.mainImage}
                    alt={relProj.title}
                    className="related-img"
                  />
                </div>

                <div className="related-card-content">
                  <div className="related-title-wrap">
                    <h3 className="related-project-title">{relProj.title}</h3>
                    <p className="related-project-subtext">{relProj.subtitle}</p>
                  </div>

                  <button
                    type="button"
                    className="see-project-pill-btn"
                    onClick={() => navigate(`/projects/${relProj.slug}`)}
                  >
                    <span className="see-project-text">SEE PROJECT</span>
                    <div className="see-project-arrow">
                      <i className="bi bi-arrow-right"></i>
                    </div>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================================================================
          7. HOME CTA SECTION
          ==================================================================== */}
      <div className="detail-cta-wrap">
        <HomeCTA />
      </div>
    </div>
  );
};

export default ProjectDetail;
